<script setup lang="ts">
import { gsap } from 'gsap'

const { t } = useI18n()
const root = ref<HTMLElement | null>(null)

onMounted(() => {
  const el = root.value
  if (!el) return

  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

  tl
    .fromTo('.pp-hero-badge',
      { opacity: 0, y: -20, scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: 0.55 },
    )
    .fromTo('.pp-hero-title',
      { opacity: 0, y: 36 },
      { opacity: 1, y: 0, duration: 0.7, ease: 'power4.out' },
      '-=0.3',
    )
    .fromTo('.pp-hero-subtitle',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6 },
      '-=0.4',
    )
    .fromTo('.pp-hero-date',
      { opacity: 0, x: 30, scale: 0.9 },
      { opacity: 1, x: 0, scale: 1, duration: 0.65, ease: 'back.out(1.5)' },
      '-=0.5',
    )
})
</script>

<template>
  <section
    id="privacy-policy-hero"
    ref="root"
    class="bg-amber-50 relative overflow-hidden pt-28 pb-16 md:pt-32 md:pb-20 lg:pt-36 lg:pb-24">

    <!-- Dot grid -->
    <div
      class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-40 pointer-events-none"
      aria-hidden="true" />

    <div class="container relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

        <!-- Left: badge + title + subtitle -->
        <div>
          <!-- Badge -->
          <div class="pp-hero-badge inline-flex items-center gap-2 bg-heart-purple text-white px-4 py-2 rounded-lg text-sm font-bold mb-5 shadow-[4px_4px_0_#111827] rotate-3">
            <UIcon name="i-heroicons-chat-bubble-oval-left-ellipsis" class="size-4 shrink-0" />
            {{ t('page.privacy_policy.hero.badge') }}
          </div>

          <!-- Title -->
          <h1 class="pp-hero-title text-[clamp(2.25rem,5vw,3.75rem)] font-bold text-deep-indigo leading-[1.1] rtl:leading-[1.35] mb-4">
            {{ t('page.privacy_policy.title') }}
          </h1>

          <!-- Subtitle -->
          <p class="pp-hero-subtitle text-sm sm:text-base lg:text-lg text-overcast-night leading-relaxed max-w-md">
            {{ t('page.privacy_policy.hero.subtitle') }}
          </p>
        </div>

        <!-- Right: last update -->
        <div class="pp-hero-date flex flex-col items-start lg:items-end gap-3">
          <span class="text-base font-bold text-gray-900">
            {{ t('page.privacy_policy.hero.last_update_label') }}
          </span>
          <div class="bg-creamy-yellow border-2 border-gray-900 rounded-lg px-6 py-3 shadow-[5px_5px_0_#111827] text-lg font-bold text-gray-900 tracking-wide">
            {{ t('page.privacy_policy.hero.last_update_date') }}
          </div>
        </div>

      </div>
    </div>
  </section>
</template>
